import { useState, useMemo, useRef, useEffect } from "react";
import { AdminLayout } from "./AdminLayout";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import { Input } from "@/project/karuna/components/ui/input"; // @ts-ignore - input is not a valid module
import { Card, CardContent } from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog"; // @ts-ignore - dialog is not a valid module
import {
  Plus,
  Search,
  MapPin,
  Phone,
  Mail,
  Edit,
  Trash2,
  Loader2,
  School,
  Users,
  FileText,
  BookOpen,
  History,
  Clock,
  Download,
  Upload,
  ArrowUpDown,
} from "lucide-react";
import { SchoolExcelImportExport } from "@/project/karuna/components/admin/SchoolExcelImportExport";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { useNavigate } from "react-router-dom";
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import { DynamicModuleService } from "@/services/APIs";
import {
  provisionSchoolLoginAccount,
  extractSchoolFormFields,
  isValidSchoolEmail,
} from "@/project/karuna/utils/schoolTenantUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createForm } from "@formily/core";
import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { getUserInfo } from "@/lib/storeManager";
import {
  KarunaPagination,
  ListCardViewToggle,
  type KarunaViewMode,
} from "@/project/karuna/components/shared/KarunaListControls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/project/karuna/components/ui/select"; // @ts-ignore - select is not a valid module
import { useKarunaPagedList } from "@/project/karuna/hooks/useKarunaPagedList";
import { fetchSchoolProfileGridRecords } from "@/project/karuna/utils/fetchSchemaGridRecords";
import {
  getSchoolRecordId,
  isSchoolListedInManagement,
} from "@/project/karuna/utils/schoolProfileApproval";
import { mapSchoolGridRecordToListItem } from "@/project/karuna/utils/schoolProfileRecordFields";
import {
  buildSchoolListRecordMap,
  clearSchoolListItemCache,
  primeSchoolListItemCache,
} from "@/project/karuna/utils/schoolProfileEnrichment";
import { useEnrichedSchoolPage } from "@/project/karuna/hooks/useEnrichedSchoolPage";

const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";

function ensureApprovedStatus(formValues: Record<string, unknown>) {
  formValues.status = "approved";
  const basicGrid = formValues.basicGrid;
  if (basicGrid && typeof basicGrid === "object") {
    (basicGrid as Record<string, unknown>).status = "approved";
  }
}
const SUBMISSION_SCHEMA_NAME = "Activity Submission";

// Helper function to normalize mobile number (remove spaces, ensure 10 digits)
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber || "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

interface SchoolData {
  id: string;
  kc_no: string;
  school_name: string;
  principal_name: string;
  contact_number: string;
  email: string;
  kendra_name: string;
  chapter_id?: string;
}

interface Stats {
  totalSchools: number;
  totalStudents: number;
  totalSubmissions: number;
  totalChapters: number;
}

type SchoolSortOption = "name" | "kc_asc" | "kc_desc";

function parseKcNumber(kcNo: string): number | null {
  const trimmed = String(kcNo ?? "").trim();
  if (!trimmed || trimmed.toUpperCase() === "N/A") {
    return null;
  }

  const digits = trimmed.replace(/\D/g, "");
  if (!digits) {
    return null;
  }

  const value = Number(digits);
  return Number.isFinite(value) ? value : null;
}

function compareSchools(
  a: SchoolData,
  b: SchoolData,
  sortBy: SchoolSortOption
): number {
  if (sortBy === "kc_asc" || sortBy === "kc_desc") {
    const kcCompare = compareKcNumbers(
      a.kc_no,
      b.kc_no,
      sortBy === "kc_asc" ? "asc" : "desc"
    );
    if (kcCompare !== 0) {
      return kcCompare;
    }
    return a.school_name.localeCompare(b.school_name);
  }

  return a.school_name.localeCompare(b.school_name);
}

function compareKcNumbers(
  a: string,
  b: string,
  direction: "asc" | "desc"
): number {
  const na = parseKcNumber(a);
  const nb = parseKcNumber(b);

  if (na === null && nb === null) {
    return 0;
  }
  if (na === null) {
    return 1;
  }
  if (nb === null) {
    return -1;
  }

  return direction === "asc" ? na - nb : nb - na;
}

const AdminSchools = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<SchoolSortOption>("kc_asc");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<SchoolData | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [viewMode, setViewMode] = useState<KarunaViewMode>("card");
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [excelDialogTab, setExcelDialogTab] = useState<"import" | "export">(
    "export"
  );
  const { toast } = useToast();
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const queryClient = useQueryClient();
  const formRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const createFormRef = useRef<{ handleSave: () => Promise<void> }>(null);

  // Fresh Formily instance per school — reset() alone keeps previous initialValues.
  const editingSchoolId = selectedSchool?.id;
  const editFormRef = useRef(createForm({}));
  const lastEditingSchoolIdRef = useRef<string | undefined>(undefined);
  if (lastEditingSchoolIdRef.current !== editingSchoolId) {
    lastEditingSchoolIdRef.current = editingSchoolId;
    editFormRef.current = createForm({});
  }
  const editForm = editFormRef.current;
  // Create form using Formily for creating new school
  const newSchoolForm = useMemo(() => createForm({}), []);

  // Fetch schools from School Profile schema
  const {
    data: schoolsData,
    isLoading: schoolsLoading,
    refetch: refetchSchools,
  } = useQuery({
    queryKey: ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await fetchSchoolProfileGridRecords();
      } catch (error) {
        console.error("Error fetching schools:", error);
        toast({
          title: "Error",
          description: "Failed to load schools. Please try again.",
          variant: "destructive",
        });
        return [];
      }
    },
    refetchOnMount: true,
    staleTime: 30 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Submission count for stats only (one lightweight request when API exposes total)
  const { data: submissionCount = 0 } = useQuery({
    queryKey: ["admin-submissions-stats-count", SUBMISSION_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridItemCount(
          SUBMISSION_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching submission count:", error);
        return 0;
      }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Transform schools data - approved + legacy rows without status in grid
  const schools = useMemo((): SchoolData[] => {
    if (!schoolsData || !Array.isArray(schoolsData)) {
      return [];
    }

    return schoolsData
      .map((school: any): SchoolData | null => {
        if (!isSchoolListedInManagement(school)) {
          return null;
        }
        const item = mapSchoolGridRecordToListItem(school);
        if (item) {
          primeSchoolListItemCache(item);
        }
        return item;
      })
      .filter((s): s is SchoolData => s !== null);
  }, [schoolsData]);

  // Calculate stats
  const stats = useMemo((): Stats => {
    const totalSchools = schools.length;

    return {
      totalSchools,
      totalStudents: 0,
      totalSubmissions: submissionCount,
      totalChapters: 0,
    };
  }, [schools, submissionCount]);

  const loading = schoolsLoading;

  const handleEditSchool = (school: SchoolData) => {
    setSelectedSchool(school);
    setEditDialogOpen(true);
  };

  const handleUpdateSchool = async () => {
    if (!formRef.current || !selectedSchool) {
      return;
    }

    setIsUpdating(true);
    try {
      // Validate form first
      await editForm.validate();

      // Get form values and remove meta fields
      const formValues = removeMetaFields(editForm.values);

      const extracted = extractSchoolFormFields(
        formValues as Record<string, unknown>
      );

      // Get the old contact number from selectedSchool before update
      const oldContactNumber = normalizeMobileNumber(
        selectedSchool.contact_number ?? ""
      );

      // Get the new contact number from form values
      const newContactNumber = normalizeMobileNumber(
        extracted.contactNumber || oldContactNumber
      );

      // Get the new school name from form values
      const newSchoolName =
        extracted.schoolName || selectedSchool.school_name || "";

      // Update the school in the School Profile schema
      await DynamicModuleService.updateItem(
        SCHOOL_PROFILE_SCHEMA_NAME,
        selectedSchool.id,
        formValues
      );

      // Sync school login user in /api/users/ (non-blocking, silent on failure)
      const schoolEmail =
        extracted.email ||
        (isValidSchoolEmail(selectedSchool.email ?? "")
          ? selectedSchool.email
          : "");
      const schoolPhone = newContactNumber || oldContactNumber;

      if (schoolEmail || schoolPhone) {
        await provisionSchoolLoginAccount({
          email: schoolEmail,
          phoneNumber: schoolPhone,
          schoolName: newSchoolName,
          oldPhoneNumber:
            oldContactNumber !== newContactNumber
              ? oldContactNumber
              : undefined,
        });
      }

      // Invalidate and refetch BEFORE closing dialog to ensure data is fresh
      await queryClient.invalidateQueries({
        queryKey: ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
      });
      clearSchoolListItemCache();
      await queryClient.refetchQueries({
        queryKey: ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
      });
      // Also explicitly refetch to ensure UI updates
      await refetchSchools();

      toast({
        title: "Success",
        description: "School details updated successfully",
      });

      // Close dialog and reset after data is refreshed
      setEditDialogOpen(false);
      setSelectedSchool(null);
      editForm.reset();

      // Check if contact number was changed and notify school users to redirect
      // Admin should NOT be redirected, only school users with matching contact number
      if (oldContactNumber && oldContactNumber !== newContactNumber) {
        try {
          // Use BroadcastChannel to notify all tabs/windows about the contact number change
          // School pages will listen for this and redirect if their contact number matches
          try {
            const channel = new BroadcastChannel("school-logout");
            channel.postMessage({
              type: "CONTACT_NUMBER_CHANGED",
              oldContactNumber: oldContactNumber,
              newContactNumber: newContactNumber,
            });
            channel.close();
            console.log(
              "📢 Broadcasted contact number change to all tabs/windows"
            );
          } catch (broadcastError) {
            console.warn("BroadcastChannel not supported:", broadcastError);
          }

          // Only check and redirect if we're on a school page (not admin page)
          // Admin pages should never redirect, they should just refresh
          if (window.location.pathname.includes("/school/")) {
            const userInfo = getUserInfo();
            // School users have phone_number in their userInfo, admin users don't
            if (userInfo?.phone_number) {
              const loggedInMobileNumber = normalizeMobileNumber(
                userInfo.phone_number
              );
              // If the logged-in school user's mobile number matches the old contact number
              if (
                loggedInMobileNumber === oldContactNumber &&
                loggedInMobileNumber !== ""
              ) {
                console.log(
                  "⚠️ Contact number changed for logged-in school user. Redirecting to auth..."
                );
                const baseUrl = window.location.origin;
                const authUrl = `${baseUrl}/school/auth`;
                // Redirect school user to auth page (admin list already refreshed)
                window.location.href = authUrl;
              }
            }
          }
          // If we're on admin page, continue with normal refresh (don't redirect)
        } catch (redirectError) {
          console.error("Error handling school user redirect:", redirectError);
          // Continue with normal flow even if redirect check fails
        }
      }

      // List was already invalidated + refetched above — the cards update in
      // place, no full-page reload needed.
    } catch (error: any) {
      console.error("Error updating school:", error);
      toast({
        title: "Error",
        description: error.message ?? "Failed to update school details",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteSchool = async (schoolId: string, schoolName: string) => {
    if (
      !confirm(
        `Are you sure you want to delete ${schoolName}? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await DynamicModuleService.deleteItem(
        SCHOOL_PROFILE_SCHEMA_NAME,
        schoolId
      );

      // Optimistic update: drop the card from the list immediately so the UI
      // reflects the delete at once, without a full-page reload.
      queryClient.setQueryData(
        ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
        (old: unknown) =>
          Array.isArray(old)
            ? old.filter(row => getSchoolRecordId(row) !== schoolId)
            : old
      );
      clearSchoolListItemCache();

      toast({
        title: "School deleted successfully",
      });

      // Reconcile with the server in the background (non-blocking).
      void queryClient.invalidateQueries({
        queryKey: ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
      });
    } catch (error: any) {
      toast({
        title: "Error deleting school",
        description: error.message ?? "Failed to delete school",
        variant: "destructive",
      });
    }
  };

  const handleCreateSchool = async () => {
    if (!createFormRef.current) {
      return;
    }

    setIsCreating(true);
    try {
      // Validate form first
      await newSchoolForm.validate();

      // Get form values
      const formValues = removeMetaFields(newSchoolForm.values);
      // Admin-created schools are always approved
      ensureApprovedStatus(formValues as Record<string, unknown>);

      const {
        schoolName,
        contactNumber,
        email: schoolEmail,
      } = extractSchoolFormFields(formValues as Record<string, unknown>);

      // Check if contact number already exists
      if (contactNumber) {
        const normalizedContact = normalizeMobileNumber(contactNumber);

        if (normalizedContact.length > 0) {
          const existingSchool = schools.find(school => {
            const existingContact = normalizeMobileNumber(
              school.contact_number
            );
            return (
              existingContact === normalizedContact &&
              existingContact.length > 0
            );
          });

          if (existingSchool) {
            toast({
              title: "Duplicate Contact Number",
              description: `A school with the contact number "${contactNumber}" already exists. Please use a different contact number.`,
              variant: "destructive",
            });
            setIsCreating(false);
            return;
          }
        }
      }

      // Create the school in the schema
      await DynamicModuleService.createItem(
        SCHOOL_PROFILE_SCHEMA_NAME,
        formValues
      );

      // Provision auth login (/api/auth/) + tenant user (/api/users/)
      let loginReady = false;
      if (contactNumber) {
        const provision = await provisionSchoolLoginAccount({
          email: schoolEmail,
          phoneNumber: contactNumber,
          schoolName: String(schoolName),
        });
        loginReady =
          provision.tenant.ok &&
          (provision.auth.ok || provision.auth.action === "skipped");
      }

      // Invalidate and refetch BEFORE closing dialog to ensure data is fresh
      await queryClient.invalidateQueries({
        queryKey: ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
      });
      clearSchoolListItemCache();
      await queryClient.refetchQueries({
        queryKey: ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
      });
      // Also explicitly refetch to ensure UI updates
      await refetchSchools();

      toast({
        title: "Success",
        description: loginReady
          ? "School created with approved status. The school can log in directly with their mobile number."
          : "School created with approved status. Add a valid 10-digit contact number to enable school login.",
      });

      // Close dialog and reset after data is refreshed (list already refetched
      // above — no full-page reload needed).
      setAddDialogOpen(false);
      newSchoolForm.reset();
    } catch (error: any) {
      // Errors are shown on the form; avoid duplicate destructive toasts
      console.error("Error creating school:", error);
    } finally {
      setIsCreating(false);
    }
  };

  // Clear selected school when dialog closes (edit form is recreated per school id).
  useEffect(() => {
    if (!editDialogOpen) {
      setSelectedSchool(null);
    }
  }, [editDialogOpen]);

  // Reset create form when dialog closes and set default status
  useEffect(() => {
    if (!addDialogOpen) {
      newSchoolForm.reset();
    } else {
      // Set default status to "approved" when dialog opens
      newSchoolForm.setInitialValues({
        status: "approved",
      });
      newSchoolForm.setValues({
        status: "approved",
      });
    }
  }, [addDialogOpen, newSchoolForm]);

  // Ensure status field is enabled and interactive in create form
  useEffect(() => {
    if (addDialogOpen && newSchoolForm) {
      // Set default status value to "approved"
      newSchoolForm.setInitialValues({
        basicGrid: {
          status: "approved",
        },
      });
      newSchoolForm.setValues({
        basicGrid: {
          status: "approved",
        },
      });

      // Function to enable status field
      const enableStatusField = () => {
        // The field path is basicGrid.status based on the schema
        const fieldPath = "basicGrid.status";

        try {
          // Try to query the field to verify it exists
          const field = newSchoolForm.query(fieldPath).take();
          if (field) {
            console.log("✅ Found status field:", fieldPath);
          }
        } catch (_e) {
          console.log("⚠️ Field not found yet:", fieldPath);
        }

        // Update field state
        newSchoolForm.setFieldState(fieldPath, (state: any) => {
          if (state) {
            // Set field-level properties
            state.disabled = false;
            state.readOnly = false;
            state.editable = true;
            state.visible = true;
            state.display = undefined;

            // CRITICAL: Set componentProps to ensure Select is interactive
            state.componentProps ??= {};

            // Ant Design Select configuration
            state.componentProps.disabled = false;
            state.componentProps.allowClear = true;
            state.componentProps.open = undefined; // Don't force open state
            state.componentProps.getPopupContainer = (
              triggerNode: HTMLElement
            ) => {
              // Ensure dropdown renders in the dialog container, not body
              return triggerNode.parentElement ?? document.body;
            };
            state.componentProps.dropdownStyle = {
              pointerEvents: "auto",
              zIndex: 1050, // Ensure dropdown is above other elements
            };

            // CRITICAL: Set pointer-events to auto to allow clicking
            state.componentProps.style = {
              pointerEvents: "auto",
              cursor: "pointer",
              ...(state.componentProps.style ?? {}),
            };

            // Remove any readOnly prop as Ant Design Select doesn't support it
            if (state.componentProps.readOnly !== undefined) {
              delete state.componentProps.readOnly;
            }

            // Ensure dataSource is set if enum exists
            if (
              state.enum &&
              Array.isArray(state.enum) &&
              state.enum.length > 0
            ) {
              const dataSource = state.enum.map((item: any) => {
                if (typeof item === "string") {
                  return { label: item, value: item };
                }
                if (item && typeof item === "object") {
                  return {
                    label: item.label ?? item.title ?? item.value ?? "",
                    value: item.value ?? item.label ?? "",
                  };
                }
                return item;
              });

              // Set dataSource if not already set or empty
              if (!state.dataSource || state.dataSource.length === 0) {
                state.dataSource = dataSource;
              }
            }

            // Also ensure the field's component type is Select
            if (
              state.component !== "Select" &&
              state.componentType !== "Select"
            ) {
              console.warn(
                "⚠️ Status field component is not Select:",
                state.component
              );
            }
          } else {
            console.warn("⚠️ Status field state is null/undefined");
          }
        });
      };

      // Use multiple timeouts to ensure field is created and updated
      const timeoutId1 = setTimeout(enableStatusField, 500);
      const timeoutId2 = setTimeout(enableStatusField, 1000);
      const timeoutId3 = setTimeout(enableStatusField, 1500);
      const timeoutId4 = setTimeout(enableStatusField, 2000);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        clearTimeout(timeoutId4);
      };
    }
  }, [addDialogOpen, newSchoolForm]);

  // Also ensure status field is enabled in edit dialog
  useEffect(() => {
    if (editDialogOpen && editForm) {
      // Use multiple timeouts to ensure field is created and updated
      const timeoutId1 = setTimeout(() => {
        // Try both field paths - status might be nested in basicGrid
        const fieldPaths = ["basicGrid.status", "status"];

        fieldPaths.forEach(path => {
          editForm.setFieldState(path, (state: any) => {
            if (state) {
              // Set field-level properties
              state.disabled = false;
              state.readOnly = false;
              state.editable = true;

              // CRITICAL: Set componentProps to ensure Select is interactive
              // Ant Design Select (used by Formily) needs componentProps.disabled to be false
              state.componentProps ??= {};
              // Ant Design Select uses 'disabled', not 'readOnly'
              state.componentProps.disabled = false;
              state.componentProps.allowClear = true;
              state.componentProps.getPopupContainer = (
                triggerNode: HTMLElement
              ) => {
                // Ensure dropdown renders in the dialog container, not body
                return triggerNode.parentElement ?? document.body;
              };
              state.componentProps.dropdownStyle = {
                pointerEvents: "auto",
                zIndex: 1050, // Ensure dropdown is above other elements
              };
              // CRITICAL: Set pointer-events to auto to allow clicking
              state.componentProps.style = {
                pointerEvents: "auto",
                cursor: "pointer",
                ...(state.componentProps.style ?? {}),
              };
              // Remove any readOnly prop as Ant Design Select doesn't support it
              if (state.componentProps.readOnly !== undefined) {
                delete state.componentProps.readOnly;
              }

              // Ensure dataSource is set if enum exists
              if (
                state.enum &&
                Array.isArray(state.enum) &&
                state.enum.length > 0
              ) {
                const dataSource = state.enum.map((item: any) => {
                  if (typeof item === "string") {
                    return { label: item, value: item };
                  }
                  if (item && typeof item === "object") {
                    return {
                      label: item.label ?? item.title ?? item.value ?? "",
                      value: item.value ?? item.label ?? "",
                    };
                  }
                  return item;
                });

                // Set dataSource if not already set or empty
                if (!state.dataSource || state.dataSource.length === 0) {
                  state.dataSource = dataSource;
                }
              }
            }
          });
        });
      }, 800);

      // Second timeout to ensure changes are applied
      const timeoutId2 = setTimeout(() => {
        const fieldPaths = ["basicGrid.status", "status"];
        fieldPaths.forEach(path => {
          // Force update by setting state again
          editForm.setFieldState(path, (state: any) => {
            if (state) {
              state.disabled = false;
              state.readOnly = false;
              if (state.componentProps) {
                state.componentProps.disabled = false;
                state.componentProps.getPopupContainer = (
                  triggerNode: HTMLElement
                ) => {
                  return triggerNode.parentElement ?? document.body;
                };
                state.componentProps.dropdownStyle = {
                  pointerEvents: "auto",
                  zIndex: 1050,
                };
                state.componentProps.style = {
                  pointerEvents: "auto",
                  cursor: "pointer",
                  ...(state.componentProps.style ?? {}),
                };
                // Remove readOnly as Ant Design Select doesn't support it
                if (state.componentProps.readOnly !== undefined) {
                  delete state.componentProps.readOnly;
                }
              }
            }
          });
        });
      }, 1500);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
      };
    }
  }, [editDialogOpen, editForm]);

  const filteredSchools = schools.filter(school => {
    const term = searchTerm.toLowerCase();
    return (
      school.school_name.toLowerCase().includes(term) ||
      school.kc_no.toLowerCase().includes(term) ||
      school.kendra_name.toLowerCase().includes(term) ||
      (school.contact_number ?? "").toLowerCase().includes(term)
    );
  });

  const sortedFilteredSchools = useMemo(() => {
    return [...filteredSchools].sort((a, b) => compareSchools(a, b, sortBy));
  }, [filteredSchools, sortBy]);

  const exportableSchoolRecords = useMemo(() => {
    if (!schoolsData || !Array.isArray(schoolsData)) {
      return [];
    }

    const filteredIds = new Set(filteredSchools.map(school => school.id));

    return schoolsData.filter((school: any) => {
      const schoolId = getSchoolRecordId(school);
      return filteredIds.has(schoolId) && isSchoolListedInManagement(school);
    });
  }, [schoolsData, filteredSchools]);

  const {
    page: schoolPage,
    setPage: setSchoolPage,
    totalPages: schoolTotalPages,
    paginatedItems: pagedSchools,
    totalItems: schoolTotalFiltered,
    pageSize: schoolPageSize,
    setPageSize: setSchoolPageSize,
  } = useKarunaPagedList(sortedFilteredSchools, searchTerm, sortBy);

  const schoolListRecordMap = useMemo(
    () => buildSchoolListRecordMap(schoolsData),
    [schoolsData]
  );

  const displaySchools = useEnrichedSchoolPage(
    pagedSchools,
    schoolListRecordMap
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-hero">
                <School className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight">
                  Schools Management
                </h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage all Karuna Clubs and school registrations
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="gap-2 bg-green-500/10 hover:bg-green-500/20"
              onClick={() =>
                navigate(getLink("/admin/school-approval?view=history"))
              }
            >
              <History className="h-4 w-4" />
              View History
            </Button>
            <Button
              variant="outline"
              className="gap-2 bg-yellow-500/10 hover:bg-yellow-500/20"
              onClick={() =>
                navigate(getLink("/admin/school-approval?tab=pending"))
              }
            >
              <Clock className="h-4 w-4" />
              View Pending Requests
            </Button>
            <Button
              variant="outline"
              className="ml-auto gap-2"
              onClick={() => {
                setExcelDialogTab("export");
                setExcelDialogOpen(true);
              }}
            >
              <Download className="h-4 w-4" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => {
                setExcelDialogTab("import");
                setExcelDialogOpen(true);
              }}
            >
              <Upload className="h-4 w-4" />
              Import Excel
            </Button>
            <Button
              className="gap-2 border-0 bg-gradient-hero shadow-md transition-all hover:shadow-lg"
              onClick={() => setAddDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Add New School
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-2 transition-all hover:shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Total Schools
                  </p>
                  <p className="text-3xl font-bold">{stats.totalSchools}</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10">
                  <School className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 transition-all hover:shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Active Students
                  </p>
                  <p className="text-3xl font-bold">{stats.totalStudents}</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500/10">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 transition-all hover:shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Total Submissions
                  </p>
                  <p className="text-3xl font-bold">{stats.totalSubmissions}</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/10">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 transition-all hover:shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Chapters
                  </p>
                  <p className="text-3xl font-bold">{stats.totalChapters}</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10">
                  <BookOpen className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <Card className="border-2">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-muted-foreground" />
              <Input
                placeholder="Search by school name, KC No, kendra, or contact..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="h-11 pl-10 text-base"
              />
            </div>
          </CardContent>
        </Card>

        {/* Schools List */}
        {loading ? (
          <Card className="border-2">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="mt-4 text-sm text-muted-foreground">
                Loading schools...
              </p>
            </CardContent>
          </Card>
        ) : filteredSchools.length === 0 ? (
          <Card className="border-2">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <School className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mt-4 text-lg font-semibold">
                {searchTerm ? "No schools found" : "No schools registered yet"}
              </h3>
              <p className="mt-2 max-w-md text-center text-sm text-muted-foreground">
                {searchTerm
                  ? "Try adjusting your search terms or clear the search to see all schools."
                  : "Get started by adding your first school to the system."}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => setAddDialogOpen(true)}
                  className="mt-6 gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add First School
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <ListCardViewToggle
                viewMode={viewMode}
                onViewChange={setViewMode}
              />
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  Sort by
                </span>
                <Select
                  value={sortBy}
                  onValueChange={value => setSortBy(value as SchoolSortOption)}
                >
                  <SelectTrigger className="h-9 w-[200px]">
                    <SelectValue placeholder="Sort schools" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">School name (A–Z)</SelectItem>
                    <SelectItem value="kc_asc">KC No (Low → High)</SelectItem>
                    <SelectItem value="kc_desc">KC No (High → Low)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {viewMode === "card" ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {displaySchools.map(school => (
                  <Card
                    key={school.id}
                    className="group border-2 transition-all hover:border-primary/50 hover:shadow-lg"
                  >
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="line-clamp-2 text-lg font-semibold leading-tight transition-colors group-hover:text-primary">
                              {school.school_name}
                            </h3>
                          </div>
                          <Badge
                            variant="secondary"
                            className="shrink-0 bg-primary/10 text-primary hover:bg-primary/20"
                          >
                            {school.kc_no}
                          </Badge>
                        </div>

                        <div className="space-y-3 border-t pt-4">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted">
                              <Users className="h-3 w-3 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                Principal
                              </p>
                              <p className="text-sm font-medium text-foreground">
                                {school.principal_name}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted">
                              <MapPin className="h-3 w-3 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                Kendra
                              </p>
                              <p className="line-clamp-1 text-sm text-foreground">
                                {school.kendra_name}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                Contact
                              </p>
                              <p className="text-sm text-foreground">
                                {school.contact_number}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                Email
                              </p>
                              <p className="line-clamp-1 text-sm text-foreground">
                                {school.email}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2 border-t pt-4">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleEditSchool(school)}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="gap-2"
                            onClick={() =>
                              handleDeleteSchool(school.id, school.school_name)
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {displaySchools.map(school => (
                  <Card
                    key={school.id}
                    className="border-2 transition-colors hover:border-primary/40"
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold">
                              {school.school_name}
                            </h3>
                            <Badge variant="secondary">{school.kc_no}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {school.principal_name} · {school.kendra_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {school.contact_number} · {school.email}
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditSchool(school)}
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() =>
                              handleDeleteSchool(school.id, school.school_name)
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <KarunaPagination
              page={schoolPage}
              totalPages={schoolTotalPages}
              totalItems={schoolTotalFiltered}
              pageSize={schoolPageSize}
              onPageChange={setSchoolPage}
              onPageSizeChange={setSchoolPageSize}
            />
          </div>
        )}

        <SchoolExcelImportExport
          open={excelDialogOpen}
          onOpenChange={setExcelDialogOpen}
          defaultTab={excelDialogTab}
          schoolRecords={exportableSchoolRecords}
          existingSchools={schools}
          onComplete={async () => {
            await queryClient.invalidateQueries({
              queryKey: ["admin-schools", SCHOOL_PROFILE_SCHEMA_NAME],
            });
            clearSchoolListItemCache();
            await refetchSchools();
          }}
        />

        {/* Edit School Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">Edit School</DialogTitle>
              <DialogDescription>
                Update school and contact information
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {selectedSchool && (
                <SchemaFormAutoSubmit
                  key={selectedSchool.id}
                  ref={formRef}
                  form={editForm}
                  schemaName={SCHOOL_PROFILE_SCHEMA_NAME}
                  recordId={selectedSchool.id}
                />
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                onClick={() => setEditDialogOpen(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUpdateSchool}
                disabled={isUpdating}
                className="bg-gradient-to-r from-primary to-primary/80 text-white hover:from-primary/90 hover:to-primary/90"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add New School Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">Add New School</DialogTitle>
              <DialogDescription>
                Fill in the school information. New schools are created as
                approved and can log in with their mobile number — no signup
                required.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <SchemaFormAutoSubmit
                key={addDialogOpen ? "open" : "closed"}
                ref={createFormRef}
                form={newSchoolForm}
                schemaName={SCHOOL_PROFILE_SCHEMA_NAME}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                onClick={() => setAddDialogOpen(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreateSchool}
                disabled={isCreating}
                className="bg-gradient-to-r from-primary to-primary/80 text-white hover:from-primary/90 hover:to-primary/90"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create School"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSchools;
